/*
 * Project: BookingEngine
 * Module: components/common/DropdownList.js
 * vim:ft=javascript.jsx:
 */
import React from 'react';
import PropTypes from 'prop-types';

import { IconComponent } from '../ds/atoms/icons/iconComponent';
import BtnClose from '../ds/atoms/btn/btnClose';
import { getLangValue } from '../../utils/Generic';

const BACKSPACE_KEY = 8;

class DropdownList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      searchPattern: '',
    };

    this.onDropdownToggle = this.onDropdownToggle.bind(this);
    this.updateSearchPattern = this.updateSearchPattern.bind(this);
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    /* Close the menu when another dropdown has been clicked and the current one is open */
    if (!this.props.isClosed && nextProps.isClosed && this.state.isOpen) {
      this.setState({ isOpen: false });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.updateSearchPattern);
  }

  updateSearchPattern(event) {
    // It generates the new searchPattern after a key has been pressed
    const keyValue = event.keyCode || event.which;
    const currentPattern = this.state.searchPattern;
    let newPattern;
    if (event.key.length === 1 && event.key.match(/^[a-zA-ZñÑ]+$/)) {
      newPattern = `${currentPattern}${event.key}`;
    } else if (keyValue === BACKSPACE_KEY && this.state.searchPattern !== '') {
      newPattern = currentPattern.substring(0, currentPattern.length - 1);
    }
    if (newPattern) {
      this.setState({ searchPattern: newPattern.toLowerCase() });
    }
  }

  getVisibleItems() {
    // It returns the visible list. When a searchPattern has been entered, we filter items containing that text
    if (this.state.searchPattern === '') {
      return this.props.items;
    }

    const pattern = this.state.searchPattern;
    return this.props.items.filter((item) => item.name.toLowerCase().includes(pattern));
  }

  addKeyEvent(willOpen) {
    if (!this.props.hasFilter) {
      return;
    }
    // When the selector can be filtered by text input, we add / remove the keydown listener
    if (willOpen) {
      window.addEventListener('keydown', this.updateSearchPattern);
    } else {
      window.removeEventListener('keydown', this.updateSearchPattern);
    }
  }

  updateStatus(willOpen) {
    const state = { isOpen: willOpen };
    if (this.props.hasFilter && !willOpen) {
      state.searchPattern = '';
    }
    this.setState(state);
  }

  onDropdownToggle() {
    const willOpen = !this.state.isOpen;
    this.addKeyEvent(willOpen);
    this.updateStatus(willOpen);
    this.props.onDropdownSelect(this.props.selectId);
  }

  selectItem(ev, item) {
    ev.preventDefault(); // stop form submit if within a Form
    this.setState({ isOpen: false });

    if (item) {
      this.props.onChange(item);
    }
  }

  getExtraClass() {
    const { extraClass } = this.props;
    if (!extraClass) {
      return '';
    }
    const classes = extraClass.split(' ');
    let str = '';
    classes.forEach((clazz) => {
      str += ` DropdownList--${clazz}`;
    });
    return str;
  }

  renderName(item, lang, isTitle) {
    if (!this.props.nameFormat) {
      if ('title' in item) {
        return getLangValue(item, lang, 'title');
      }
      return getLangValue(item, lang, 'name');
    }
    return this.props.nameFormat(item, lang, isTitle, this.state.isOpen, this.context);
  }

  renderSelectedName() {
    return this.renderName(this.props.selectedItem, this.props.lang, true) || ' ';
  }

  renderSearchPattern() {
    return this.state.searchPattern ? (
      <span>{` (${this.state.searchPattern})`}</span>
    ) : null;
  }

  renderTitle() {
    document.addEventListener('mousedown', this.handleClickOutside.bind(this));
    return (
      <React.Fragment>
        <span className="title">{this.props.title}{this.renderSearchPattern()} </span>
        <BtnClose onClick={(ev) => this.selectItem(ev, null)} className="md:hidden"/>
      </React.Fragment>
    );
  }

  handleClickOutside(event) {
    const clickedOutside = !($('.DropdownList-container').find(event.target).length !== 0);
    if (clickedOutside) {
      this.setState({ isOpen: false });
    }
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  render() {
    const { isOpen } = this.state;
    return (
      <div className="DropdownList-container h-full flex items-center">
        {/*<div className={`DropdownList ${this.getExtraClass()} w-36 ${isOpen ? 'active' : ''}`}*/}
        <div className={`DropdownList ${this.getExtraClass()} ${isOpen ? 'active' : ''}`}
             onClick={this.onDropdownToggle}>
          <div className={`DropdownList__title px-2 py-1 ${isOpen ? 'py-2' : 'py-1'}`}>
            {isOpen ? this.renderTitle() : this.renderSelectedName()}

            {!isOpen && <IconComponent icon="arrow" className="ml-2"/>}
          </div>
          <ul className="DropdownListItems RadioSelect">
            {this.getVisibleItems().map((item) => {
              const isSelected = item.code === this.props.selectedItem.code;
              const isLangSelected = this.props.variant === 'language' && isSelected;
              return (
                <li key={`${this.props.selectId}_${item.code}`} onClick={(ev) => this.selectItem(ev, item)}
                    className={`RadioSelect__item ${isSelected ? 'selected' : ''}`}>
                  <button className="RadioSelect__item-link">
                    {item.code === 'close_session' && <i className={'fa fa-ban text-primary'} aria-hidden="true"/> }
                    {this.renderName(item, this.props.lang, false)}
                    {(isLangSelected || isSelected) && <IconComponent icon="check-tick" className="ml-2 self-center"/>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }
}

DropdownList.propTypes = {
  selectId: PropTypes.string.isRequired,
  title: PropTypes.string,
  items: PropTypes.array.isRequired,
  selectedItem: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onDropdownSelect: PropTypes.func,
  lang: PropTypes.string.isRequired,
  nameFormat: PropTypes.func,
  isClosed: PropTypes.bool,
  extraClass: PropTypes.string,
  hasFilter: PropTypes.bool,
};

DropdownList.defaultProps = {
  onDropdownSelect: () => null,
  isClosed: false,
  extraClass: '',
};

DropdownList.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default DropdownList;
